import React, { useState, useRef, useEffect, useCallback } from "react";
import { INSTRUMENTS, TIMEFRAMES, BASE_PRICES, fmt, TF_VOL, TF_INTERVALS, TF_MS } from "./constants";
import { EVENT_TYPES } from "./EconomicCalendar";

// ─── CANDLE CHART — TradingView style ────────────────────────
const CandleChart = ({ symbol, timeframe, livePrice, priceRef, settings, onContextMenu, positions, activeTool, setActiveTool, drawingsRef, priceAlerts=[], indicators=[], economicEvents=[] }) => {
  const candles = useRef([]);
  const [, setTick] = useState(0);
  const [offset, setOffset] = useState(0);   // bars scrolled from right
  const [zoom, setZoom] = useState(80);       // bars visible
  // Indicator helpers
  const calcEMA = (data, period) => {
    const k = 2 / (period + 1);
    const result = [];
    let ema = data[0]?.c ?? 0;
    data.forEach((c, i) => {
      if (i === 0) { result.push(ema); return; }
      ema = c.c * k + ema * (1 - k);
      result.push(ema);
    });
    return result;
  };
  const calcSMA = (data, period) => data.map((_, i) => {
    if (i < period - 1) return null;
    return data.slice(i - period + 1, i + 1).reduce((s, c) => s + c.c, 0) / period;
  });
  const calcVWAP = (data) => {
    // Rolling daily VWAP (resets at start of data for simplicity)
    let cumPV = 0, cumVol = 0;
    return data.map(c => {
      const tp = (c.h + c.l + c.c) / 3;
      cumPV += tp * (c.vol || 1);
      cumVol += (c.vol || 1);
      return cumVol > 0 ? cumPV / cumVol : tp;
    });
  };
  const calcBollinger = (data, period, mult) => {
    const sma = calcSMA(data, period);
    return data.map((_, i) => {
      if (sma[i] == null) return null;
      const slice = data.slice(Math.max(0, i - period + 1), i + 1);
      const mean = sma[i];
      const std = Math.sqrt(slice.reduce((s, c) => s + (c.c - mean) ** 2, 0) / slice.length);
      return { mid: mean, upper: mean + mult * std, lower: mean - mult * std };
    });
  };
  const calcRSI = (data, period) => {
    const changes = data.map((c, i) => i === 0 ? 0 : c.c - data[i - 1].c);
    return data.map((_, i) => {
      if (i < period) return null;
      const slice = changes.slice(i - period + 1, i + 1);
      const gains = slice.filter(x => x > 0).reduce((s, x) => s + x, 0) / period;
      const losses = slice.filter(x => x < 0).reduce((s, x) => s + Math.abs(x), 0) / period;
      if (losses === 0) return 100;
      const rs = gains / losses;
      return 100 - 100 / (1 + rs);
    });
  };
  const canvasRef = useRef(null);
  const isDragging = useRef(false);
  const isScalingPrice = useRef(false);
  const isScalingTime = useRef(false);
  const dragStart = useRef(null);
  const [crosshair, setCrosshair] = useState(null);   // {x,y} in canvas pixels
  const [drawings, setDrawings] = useState([]);
  const [drawingState, setDrawingState] = useState(null);
  const [ohlc, setOhlc] = useState(null);
  const [priceAxisW, setPriceAxisW] = useState(72);
  const [timeAxisH, setTimeAxisH] = useState(24);
  const [priceOffset, setPriceOffset] = useState(0);  // price units to shift view up/down
  const animFrame = useRef(null);

  // Expose API to parent
  useEffect(() => {
    if (drawingsRef) drawingsRef.current = {
      clear: () => setDrawings([]),
      resetZoom: () => { setZoom(80); setOffset(0); }
    };
  }, []);

  // Generate candle history
  useEffect(() => {
    const base = BASE_PRICES[symbol] || 100;
    const vol = base * TF_VOL[timeframe];
    const arr = []; let p = base;
    for (let i = 260; i >= 0; i--) {
      const o = p + (Math.random() - 0.5) * vol * 0.25;
      const c = o + (Math.random() - 0.5) * vol;
      arr.push({
        o, c,
        h: Math.max(o, c) + Math.random() * vol * 0.35,
        l: Math.min(o, c) - Math.random() * vol * 0.35,
        vol: 0.15 + Math.random() * 0.85,
        t: Date.now() - i * (TF_MS[timeframe] || 300000),
      });
      p = c;
    }
    candles.current = arr;
    setOffset(0);
    setPriceOffset(0);
    setDrawings([]);
  }, [symbol, timeframe]);

  // Live tick
  useEffect(() => {
    if (livePrice != null) return;
    const base = BASE_PRICES[symbol] || 100;
    const vol = base * TF_VOL[timeframe] * 0.12;
    const iv = setInterval(() => {
      const last = candles.current[candles.current.length - 1];
      if (!last) return;
      const nc = last.c + (Math.random() - 0.488) * vol;
      candles.current[candles.current.length - 1] = {
        ...last, c: nc,
        h: Math.max(last.h, nc),
        l: Math.min(last.l, nc),
      };
      if (priceRef) priceRef.current = nc;
      setTick(t => t + 1);
    }, TF_INTERVALS[timeframe]);
    return () => clearInterval(iv);
  }, [symbol, timeframe, livePrice]);

  useEffect(() => {
    if (livePrice == null || !candles.current.length) return;
    const last = candles.current[candles.current.length - 1];
    candles.current[candles.current.length - 1] = {
      ...last, c: livePrice,
      h: Math.max(last.h, livePrice),
      l: Math.min(last.l, livePrice),
    };
    if (priceRef) priceRef.current = livePrice;
    setTick(t => t + 1);
  }, [livePrice]);

  // Wheel zoom
  useEffect(() => {
    const el = canvasRef.current; if (!el) return;
    const onW = (e) => {
      e.preventDefault();
      if (e.ctrlKey || e.metaKey) {
        // Ctrl+Scroll = zoom (change bars visible)
        setZoom(z => Math.max(20, Math.min(400, z + (e.deltaY > 0 ? 8 : -8))));
      } else {
        // Regular scroll = pan left/right
        setOffset(o => Math.max(0, o + (e.deltaY > 0 ? -3 : 3)));
      }
    };
    el.addEventListener('wheel', onW, { passive: false });
    return () => el.removeEventListener('wheel', onW);
  }, []);

  // Keyboard
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') { setActiveTool(null); setDrawingState(null); }
      if (e.key === 'h') setActiveTool(t => t === 'hline' ? null : 'hline');
      if (e.key === 't') setActiveTool(t => t === 'trendline' ? null : 'trendline');
      if (e.key === 'f') setActiveTool(t => t === 'fib' ? null : 'fib');
      if (e.key === 'r') setActiveTool(t => t === 'rect' ? null : 'rect');
      if (e.key === 'd') setDrawings([]);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // ── DRAW ──────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const DPR = window.devicePixelRatio || 1;
    const W = canvas.width / DPR;
    const H = canvas.height / DPR;
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);

    // ── Layout
    const PRICE_W = settings.showPriceScale ? priceAxisW : 0;
    const TIME_H = settings.showTimeScale ? timeAxisH : 0;
    const VOL_H = settings.showVolume ? Math.floor(H * 0.12) : 0;
    const chartL = 0, chartR = W - PRICE_W;
    const chartT = 2, chartB = H - TIME_H - VOL_H - 2;
    const chartW = chartR - chartL;
    const chartH = chartB - chartT;
    const volT = chartB + 4;
    const volB = H - TIME_H - 2;

    // ── Visible candles  (timeZoom shrinks/grows the visible bar count)
    const total = candles.current.length;
    const startIdx = Math.max(0, total - zoom - offset);
    const endIdx = total - offset;
    const vis = candles.current.slice(startIdx, endIdx || undefined).slice(-zoom);
    if (!vis.length) return;

    const prices = vis.flatMap(c => [c.h, c.l]);
    const minP = Math.min(...prices);
    const maxP = Math.max(...prices);
    const pad = (maxP - minP) * 0.06;
    const lo = minP - pad - priceOffset, hi = maxP + pad - priceOffset;
    const priceRange = hi - lo || 1;

    const barW = chartW / (vis.length || 1);
    const bodyW = Math.max(1, barW * 0.62);

    const py = (p) => chartT + chartH - ((p - lo) / priceRange) * chartH;
    const bx = (i) => chartL + i * barW + barW / 2;

    const last = vis[vis.length - 1];
    if (priceRef && !priceRef.current) priceRef.current = last.c;

    // ── Background
    ctx.fillStyle = settings.bgColor;
    ctx.fillRect(0, 0, W, H);

    // ── Watermark
    if (settings.watermark !== 'hidden') {
      ctx.save();
      ctx.globalAlpha = 0.04;
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${Math.floor(chartW / 8)}px monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(settings.watermark === 'symbol' ? symbol : 'CLEARFUND', chartL + chartW / 2, chartT + chartH / 2);
      ctx.restore();
    }

    // ── Price grid lines + labels
    const nGridLines = 8;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    for (let i = 0; i <= nGridLines; i++) {
      const r = i / nGridLines;
      const p = hi - r * priceRange;
      const y = Math.round(chartT + r * chartH) + 0.5;

      if (settings.gridLines !== 'none') {
        ctx.beginPath();
        ctx.strokeStyle = settings.scaleLinesColor;
        ctx.lineWidth = 1;
        if (settings.gridLines === 'dashed') {
          ctx.setLineDash([5, 4]);
        } else {
          ctx.setLineDash([]);
        }
        ctx.moveTo(chartL, y);
        ctx.lineTo(chartR, y);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      if (settings.showPriceScale) {
        // Tick mark
        ctx.fillStyle = settings.scaleLinesColor;
        ctx.fillRect(chartR, y - 0.5, 4, 1);
        // Label
        ctx.fillStyle = settings.scaleTextColor;
        ctx.font = `${settings.scaleTextSize}px 'IBM Plex Mono', monospace`;
        ctx.textAlign = 'left';
        ctx.fillText(fmt.price(p, symbol), chartR + 6, y);
      }
    }

    // Vertical separator between chart and price axis
    if (settings.showPriceScale) {
      // Hover highlight on price axis
      const inPriceAxis = crosshair && crosshair.x >= chartR && crosshair.y <= chartB;
      if (inPriceAxis || isScalingPrice.current) {
        ctx.fillStyle = 'rgba(255,255,255,0.04)';
        ctx.fillRect(chartR, chartT, PRICE_W, chartB - chartT);
        // resize hint arrow
        ctx.fillStyle = 'rgba(255,255,255,0.2)';
        ctx.font = '12px monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('↕', chartR + PRICE_W / 2, chartT + chartH / 2);
      }
      ctx.beginPath();
      ctx.strokeStyle = inPriceAxis || isScalingPrice.current
        ? 'rgba(0,200,150,0.5)' : settings.scaleLinesColor;
      ctx.lineWidth = 1;
      ctx.setLineDash([]);
      ctx.moveTo(chartR + 0.5, chartT);
      ctx.lineTo(chartR + 0.5, chartB);
      ctx.stroke();
    }

    // ── Volume bars
    if (settings.showVolume && VOL_H > 0) {
      const volH = volB - volT;
      vis.forEach((c, i) => {
        const x = bx(i);
        const up = c.c >= c.o;
        const bh = Math.max(1, c.vol * volH * 0.9);
        ctx.fillStyle = up ? settings.volumeUpColor : settings.volumeDownColor;
        ctx.fillRect(x - bodyW / 2, volB - bh, bodyW, bh);
      });
      // Volume area separator
      ctx.beginPath();
      ctx.strokeStyle = settings.scaleLinesColor;
      ctx.lineWidth = 1;
      ctx.setLineDash([]);
      ctx.moveTo(chartL, volT - 2);
      ctx.lineTo(chartR, volT - 2);
      ctx.stroke();
    }

    // ── Area fill (if area mode)
    if (settings.candleStyle === 'area' && vis.length > 1) {
      const grad = ctx.createLinearGradient(0, chartT, 0, chartB);
      grad.addColorStop(0, settings.upColor + '55');
      grad.addColorStop(1, settings.upColor + '00');
      ctx.beginPath();
      ctx.moveTo(bx(0), chartB);
      vis.forEach((c, i) => ctx.lineTo(bx(i), py(c.c)));
      ctx.lineTo(bx(vis.length - 1), chartB);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();
      // Line
      ctx.beginPath();
      vis.forEach((c, i) => i === 0 ? ctx.moveTo(bx(i), py(c.c)) : ctx.lineTo(bx(i), py(c.c)));
      ctx.strokeStyle = settings.upColor;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([]);
      ctx.stroke();
    }

    // ── Candles / bars / line
    vis.forEach((c, i) => {
      const up = c.c >= c.o;
      const x = bx(i);
      const upCol = settings.upColor;
      const dnCol = settings.downColor;
      const col = up ? upCol : dnCol;

      if (settings.candleStyle === 'line') {
        if (i === 0) return;
        ctx.beginPath();
        ctx.strokeStyle = upCol;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([]);
        ctx.moveTo(bx(i - 1), py(vis[i - 1].c));
        ctx.lineTo(x, py(c.c));
        ctx.stroke();
        return;
      }
      if (settings.candleStyle === 'area') return; // drawn above

      if (settings.candleStyle === 'bar') {
        ctx.strokeStyle = col;
        ctx.lineWidth = settings.wickWidth;
        ctx.setLineDash([]);
        // Stem
        ctx.beginPath(); ctx.moveTo(x, py(c.h)); ctx.lineTo(x, py(c.l)); ctx.stroke();
        // Open tick
        ctx.beginPath(); ctx.moveTo(x - bodyW / 2, py(c.o)); ctx.lineTo(x, py(c.o)); ctx.stroke();
        // Close tick
        ctx.beginPath(); ctx.moveTo(x, py(c.c)); ctx.lineTo(x + bodyW / 2, py(c.c)); ctx.stroke();
        return;
      }

      // Candle (solid or hollow)
      const wickCol = up ? settings.upWickColor : settings.downWickColor;
      const borderCol = up ? settings.upBorderColor : settings.downBorderColor;
      const bodyTop = py(Math.max(c.o, c.c));
      const bodyBot = py(Math.min(c.o, c.c));
      const bH = Math.max(1, bodyBot - bodyTop);

      // Wick
      ctx.beginPath();
      ctx.strokeStyle = wickCol;
      ctx.lineWidth = Math.max(1, settings.wickWidth);
      ctx.setLineDash([]);
      ctx.moveTo(x, py(c.h));
      ctx.lineTo(x, bodyTop);
      ctx.moveTo(x, bodyBot);
      ctx.lineTo(x, py(c.l));
      ctx.stroke();

      // Body
      if (settings.candleStyle === 'hollow') {
        ctx.strokeStyle = borderCol;
        ctx.lineWidth = Math.max(1, settings.borderWidth);
        ctx.strokeRect(x - bodyW / 2, bodyTop, bodyW, bH);
      } else {
        ctx.fillStyle = col;
        ctx.fillRect(x - bodyW / 2, bodyTop, bodyW, bH);
        if (settings.borderWidth > 0) {
          ctx.strokeStyle = borderCol;
          ctx.lineWidth = settings.borderWidth;
          ctx.strokeRect(x - bodyW / 2, bodyTop, bodyW, bH);
        }
      }
    });

    // ── Indicator overlays (EMA / SMA)
    indicators.forEach(ind => {
      if (!ind.visible) return;
      const allCandles = candles.current;
      const totalC = allCandles.length;
      if (totalC < (ind.period || 1)) return;
      const visStart = Math.max(0, totalC - zoom - offset);
      const visEnd = totalC - offset;
      const visSlice = allCandles.slice(visStart, visEnd || undefined).slice(-zoom);
      const globalStart = totalC - offset - visSlice.length;

      const drawLine = (values, color, lineW=1.5, dashArr=[]) => {
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = lineW;
        ctx.setLineDash(dashArr);
        let started = false;
        visSlice.forEach((_, i) => {
          const gi = globalStart + i;
          const val = values[gi];
          if (val == null) return;
          const x = bx(i); const y = py(val);
          if (!started) { ctx.moveTo(x, y); started = true; } else ctx.lineTo(x, y);
        });
        ctx.stroke();
        ctx.setLineDash([]);
        return started;
      };

      if (ind.type === 'VWAP') {
        const vals = calcVWAP(allCandles);
        const ok = drawLine(vals, ind.color, 1.5, []);
        if (ok) {
          const lv = vals[globalStart + visSlice.length - 1];
          if (lv != null) { const ly = py(lv); if (ly > chartT && ly < chartB) { ctx.font='bold 9px monospace'; ctx.fillStyle=ind.color; ctx.textAlign='left'; ctx.fillText('VWAP', Math.min(bx(visSlice.length-1)+barW, chartR-2), ly-3); } }
        }
      } else if (ind.type === 'BB') {
        const bbs = calcBollinger(allCandles, ind.period || 20, ind.mult || 2);
        drawLine(bbs.map(b => b?.upper), ind.color, 1, [4, 3]);
        drawLine(bbs.map(b => b?.mid),   ind.color, 1, []);
        drawLine(bbs.map(b => b?.lower), ind.color, 1, [4, 3]);
        // Fill between bands
        ctx.beginPath();
        let s=false;
        visSlice.forEach((_,i)=>{ const gi=globalStart+i; const b=bbs[gi]; if(!b) return; const x=bx(i),y=py(b.upper); if(!s){ctx.moveTo(x,y);s=true;}else ctx.lineTo(x,y); });
        [...visSlice].reverse().forEach((_,ri)=>{ const i=visSlice.length-1-ri; const gi=globalStart+i; const b=bbs[gi]; if(!b) return; ctx.lineTo(bx(i),py(b.lower)); });
        ctx.closePath();
        ctx.fillStyle = ind.color.replace(')', ',0.05)').replace('rgb(','rgba(').replace('#', '') === ind.color ? ind.color + '12' : ind.color;
        ctx.globalAlpha = 0.07;
        ctx.fill();
        ctx.globalAlpha = 1;
        // Label
        const lbb = bbs[globalStart + visSlice.length - 1];
        if (lbb) { const ly=py(lbb.upper); if(ly>chartT&&ly<chartB){ctx.font='bold 9px monospace';ctx.fillStyle=ind.color;ctx.textAlign='left';ctx.fillText(`BB${ind.period||20}`,Math.min(bx(visSlice.length-1)+barW,chartR-2),ly-3);} }
      } else {
        // EMA / SMA
        const rawValues = ind.type === 'EMA' ? calcEMA(allCandles, ind.period) : calcSMA(allCandles, ind.period);
        const ok = drawLine(rawValues, ind.color, 1.5, ind.dash ? [6,3] : []);
        if (ok) {
          const lv = rawValues[globalStart + visSlice.length - 1];
          if (lv != null) { const ly=py(lv); if(ly>chartT&&ly<chartB){ctx.font='bold 9px monospace';ctx.fillStyle=ind.color;ctx.textAlign='left';ctx.fillText(`${ind.type}${ind.period}`,Math.min(bx(visSlice.length-1)+barW,chartR-2),ly-3);} }
        }
      }
    });

    // ── Position lines
    if (settings.showPositionLines) {
      positions.forEach(pos => {
        const ep = parseFloat(pos.entryPrice);
        const y = Math.round(py(ep)) + 0.5;
        if (y < chartT || y > chartB) return;
        const isLong = pos.side === 'LONG';
        const lineCol = settings.positionLineColor;
        ctx.beginPath();
        ctx.strokeStyle = lineCol;
        ctx.lineWidth = 1;
        ctx.setLineDash([8, 4]);
        ctx.moveTo(chartL, y); ctx.lineTo(chartR, y);
        ctx.stroke();
        ctx.setLineDash([]);
        // Label pill
        const label = `${isLong ? '▲ L' : '▼ S'} ${ep.toFixed(2)}`;
        ctx.font = 'bold 13px monospace';
        const tw = ctx.measureText(label).width;
        ctx.fillStyle = lineCol;
        ctx.fillRect(chartL + 4, y - 9, tw + 10, 18);
        ctx.fillStyle = '#000';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, chartL + 9, y);
      });
    }

    // ── Session lines (daily open)
    if (vis.length > 0) {
      // Find candles that start a new day
      vis.forEach((c, i) => {
        if (i === 0) return;
        const prevDate = new Date(vis[i-1].t);
        const curDate = new Date(c.t);
        if (curDate.getDate() !== prevDate.getDate()) {
          const x = Math.round(bx(i)) + 0.5;
          ctx.beginPath();
          ctx.strokeStyle = 'rgba(255,255,255,0.08)';
          ctx.lineWidth = 1;
          ctx.setLineDash([3, 5]);
          ctx.moveTo(x, chartT); ctx.lineTo(x, chartB);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = '#2a2d3a';
          ctx.font = '12px monospace';
          ctx.textAlign = 'center';
          ctx.fillText(curDate.toLocaleDateString('en', {month:'numeric',day:'numeric'}), x, chartT + 10);
        }
      });
    }

    // ── Economic events — vertical lines + flags
    if (economicEvents.length > 0 && vis.length > 0) {
      const visStart = vis[0].t;
      const visEnd = vis[vis.length - 1].t;
      const tfMs = (visEnd - visStart) / Math.max(vis.length - 1, 1);
      // Group events that land on same candle to avoid overlap
      const slotMap = {};
      economicEvents.forEach(ev => {
        if (ev.ts < visStart - tfMs || ev.ts > visEnd + tfMs) return;
        // Find nearest candle by linear interpolation
        const rawIdx = (ev.ts - visStart) / tfMs;
        const nearestIdx = Math.max(0, Math.min(vis.length - 1, Math.round(rawIdx)));
        const slot = nearestIdx;
        if (!slotMap[slot]) slotMap[slot] = [];
        slotMap[slot].push(ev);
      });
      Object.entries(slotMap).forEach(([slotIdx, evList]) => {
        const idx = parseInt(slotIdx);
        const x = Math.round(bx(idx)) + 0.5;
        // Draw vertical line for highest-impact event
        const primary = evList[0];
        const evType = EVENT_TYPES[primary.type];
        if (!evType) return;
        const color = evType.color;
        const isPast = primary.ts < Date.now();
        ctx.save();
        ctx.globalAlpha = isPast ? 0.4 : 0.85;
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 4]);
        ctx.beginPath();
        ctx.moveTo(x, chartT + 16);
        ctx.lineTo(x, chartB);
        ctx.stroke();
        ctx.setLineDash([]);
        // Draw stacked flags for each event
        evList.slice(0, 3).forEach((ev, fi) => {
          const et = EVENT_TYPES[ev.type];
          if (!et) return;
          const flagW = 24, flagH = 12;
          const flagX = x - flagW / 2;
          const flagY = chartT + 2 + fi * 13;
          ctx.globalAlpha = isPast ? 0.35 : 0.9;
          ctx.fillStyle = et.color;
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(flagX, flagY, flagW, flagH, 2);
          else ctx.rect(flagX, flagY, flagW, flagH);
          ctx.fill();
          ctx.globalAlpha = 1;
          ctx.fillStyle = '#000';
          ctx.font = 'bold 7px monospace';
          ctx.textAlign = 'center';
          ctx.fillText(et.short, x, flagY + 8.5);
        });
        ctx.restore();
      });
    }

    // ── Price alerts
    if (priceAlerts.length) {
      priceAlerts.forEach(a => {
        const ay = Math.round(py(a.price)) + 0.5;
        if (ay < chartT || ay > chartB) return;
        ctx.beginPath();
        ctx.strokeStyle = '#a78bfa';
        ctx.lineWidth = 1;
        const dashStyle = settings.alertLineStyle === 'dotted' ? [2, 4] : settings.alertLineStyle === 'solid' ? [] : [6, 4];
        ctx.setLineDash(dashStyle);
        ctx.globalAlpha = 0.75;
        ctx.moveTo(chartL, ay); ctx.lineTo(chartR, ay);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;
        // Bell label
        const alabel = `🔔 ${a.price.toFixed(2)}`;
        ctx.font = 'bold 13px monospace';
        const atw = ctx.measureText(alabel).width;
        ctx.fillStyle = 'rgba(167,139,250,0.2)';
        ctx.strokeStyle = 'rgba(167,139,250,0.6)';
        ctx.lineWidth = 1;
        const ax = chartR - atw - 20;
        ctx.fillRect(ax, ay - 9, atw + 12, 18);
        ctx.strokeRect(ax, ay - 9, atw + 12, 18);
        ctx.fillStyle = '#a78bfa';
        ctx.fillText(alabel, ax + 6, ay + 3);
      });
    }

    // ── Current price dash + pill
    const lastPrice = last.c;
    const lpy = Math.round(py(lastPrice)) + 0.5;
    const priceIsUp = last.c >= last.o;
    const pillCol = priceIsUp ? settings.upColor : settings.downColor;

    ctx.beginPath();
    ctx.strokeStyle = pillCol;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 3]);
    ctx.moveTo(chartL, lpy); ctx.lineTo(chartR, lpy);
    ctx.stroke();
    ctx.setLineDash([]);

    if (settings.showPriceScale) {
      const priceLabel = fmt.price(lastPrice, symbol);
      ctx.font = 'bold 12px monospace';
      const tw = ctx.measureText(priceLabel).width;
      const pillW = tw + 14;
      ctx.fillStyle = pillCol;
      ctx.beginPath();
      ctx.roundRect(chartR + 1, lpy - 10, pillW, 20, 2);
      ctx.fill();
      ctx.fillStyle = priceIsUp ? '#000' : '#fff';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(priceLabel, chartR + 7, lpy);
    }

    // ── Crosshair cursor price label (y axis)
    if (crosshair && settings.crosshairStyle !== 'none' && settings.showPriceScale) {
      const cy = crosshair.y;
      const cp = lo + (1 - (cy - chartT) / chartH) * priceRange;
      if (cy >= chartT && cy <= chartB) {
        ctx.fillStyle = '#2a2e3d';
        ctx.beginPath();
        ctx.roundRect(chartR + 1, cy - 10, 70, 20, 2);
        ctx.fill();
        ctx.strokeStyle = 'rgba(255,255,255,0.2)';
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = '#c8ccd7';
        ctx.font = '11px monospace';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(fmt.price(cp, symbol), chartR + 6, cy);
      }
    }

    // ── Crosshair time label (x axis)
    if (crosshair && settings.crosshairStyle !== 'none' && settings.showTimeScale) {
      const cx = crosshair.x;
      if (cx >= chartL && cx <= chartR) {
        const ci = Math.floor(cx / barW);
        const bar = ci >= 0 && ci < vis.length ? vis[ci] : null;
        if (bar) {
          const d = new Date(bar.t);
          const lbl = timeframe.includes('D') || timeframe.includes('W')
            ? d.toLocaleDateString('en-US',{month:'short',day:'numeric'})
            : d.toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:false});
          ctx.font = '11px monospace';
          const tw = ctx.measureText(lbl).width + 10;
          const bx2 = Math.min(Math.max(cx - tw/2, chartL), chartR - tw);
          const by = H - TIME_H + 1;
          ctx.fillStyle = '#2a2e3d';
          ctx.beginPath();
          if (ctx.roundRect) ctx.roundRect(bx2, by, tw, 16, 2); else ctx.rect(bx2, by, tw, 16);
          ctx.fill();
          ctx.strokeStyle = 'rgba(255,255,255,0.2)'; ctx.lineWidth=1; ctx.stroke();
          ctx.fillStyle = '#c8ccd7'; ctx.textAlign='center'; ctx.textBaseline='middle';
          ctx.fillText(lbl, bx2 + tw/2, by + 8);
        }
      }
    }

    // ── Daily reference lines (high, low, prev close)
    if (vis.length > 1) {
      // Find today's session (last trading day)
      const lastBar = vis[vis.length-1];
      const today = new Date(lastBar.t).toDateString();
      const todayBars = vis.filter(c => new Date(c.t).toDateString() === today);
      if (todayBars.length > 0) {
        const dHigh = Math.max(...todayBars.map(c=>c.h));
        const dLow  = Math.min(...todayBars.map(c=>c.l));
        // Day high
        const dhY = Math.round(py(dHigh))+0.5;
        if (dhY >= chartT && dhY <= chartB) {
          ctx.beginPath(); ctx.strokeStyle='rgba(0,200,150,0.35)'; ctx.lineWidth=1;
          ctx.setLineDash([3,5]); ctx.moveTo(chartL,dhY); ctx.lineTo(chartR,dhY); ctx.stroke();
          ctx.setLineDash([]);
          ctx.font='8px monospace'; ctx.fillStyle='rgba(0,200,150,0.7)'; ctx.textAlign='left';
          ctx.fillText('D.Hi '+fmt.price(dHigh,symbol), chartL+3, dhY-3);
        }
        // Day low
        const dlY = Math.round(py(dLow))+0.5;
        if (dlY >= chartT && dlY <= chartB) {
          ctx.beginPath(); ctx.strokeStyle='rgba(240,59,59,0.35)'; ctx.lineWidth=1;
          ctx.setLineDash([3,5]); ctx.moveTo(chartL,dlY); ctx.lineTo(chartR,dlY); ctx.stroke();
          ctx.setLineDash([]);
          ctx.font='8px monospace'; ctx.fillStyle='rgba(240,59,59,0.7)'; ctx.textAlign='left';
          ctx.fillText('D.Lo '+fmt.price(dLow,symbol), chartL+3, dlY+9);
        }
        // Previous close — last bar of previous day
        const prevDayBars = vis.filter(c => new Date(c.t).toDateString() !== today);
        if (prevDayBars.length > 0) {
          const prevClose = prevDayBars[prevDayBars.length-1].c;
          const pcY = Math.round(py(prevClose))+0.5;
          if (pcY >= chartT && pcY <= chartB) {
            ctx.beginPath(); ctx.strokeStyle='rgba(245,166,35,0.4)'; ctx.lineWidth=1;
            ctx.setLineDash([6,4]); ctx.moveTo(chartL,pcY); ctx.lineTo(chartR,pcY); ctx.stroke();
            ctx.setLineDash([]);
            ctx.font='8px monospace'; ctx.fillStyle='rgba(245,166,35,0.7)'; ctx.textAlign='left';
            ctx.fillText('PC '+fmt.price(prevClose,symbol), chartL+3, pcY-3);
          }
        }
      }
    }

    // ── Drawings
    const drawHline = (price, color, dash) => {
      const y = Math.round(py(price)) + 0.5;
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.setLineDash(dash);
      ctx.moveTo(chartL, y); ctx.lineTo(chartR, y);
      ctx.stroke();
      ctx.setLineDash([]);
      if (settings.showPriceScale) {
        ctx.fillStyle = color + 'cc';
        ctx.beginPath();
        ctx.roundRect(chartR + 1, y - 8, 68, 16, 2);
        ctx.fill();
        ctx.fillStyle = '#000';
        ctx.font = '12px monospace';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(fmt.price(price, symbol), chartR + 5, y);
      }
    };

    drawings.forEach(d => {
      if (d.type === 'hline') drawHline(d.price, '#F5A623', [6, 3]);
      if (d.type === 'trendline') {
        ctx.beginPath();
        ctx.strokeStyle = '#4DB8FF';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([]);
        ctx.moveTo(d.p1.x, d.p1.y); ctx.lineTo(d.p2.x, d.p2.y);
        ctx.stroke();
        [d.p1, d.p2].forEach(pt => {
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, 3, 0, Math.PI * 2);
          ctx.fillStyle = '#4DB8FF';
          ctx.fill();
        });
      }
      if (d.type === 'fib') {
        const FIB = [0,0.236,0.382,0.5,0.618,0.786,1.0];
        const FCOLS = ['#f03b3b','#f5a623','#f5a623','#00c896','#00c896','#4db8ff','#4db8ff'];
        const hi = Math.max(d.p1.price, d.p2.price);
        const lo = Math.min(d.p1.price, d.p2.price);
        const rng = hi - lo || 1;
        const x1 = Math.min(d.p1.x, d.p2.x);
        const x2 = Math.max(d.p1.x, d.p2.x);
        // golden zone fill
        const y382 = py(hi - 0.382*rng), y618 = py(hi - 0.618*rng);
        ctx.fillStyle = 'rgba(0,200,150,0.05)';
        ctx.fillRect(x1, Math.min(y382,y618), x2-x1, Math.abs(y618-y382));
        FIB.forEach((lvl, li) => {
          const price = hi - lvl*rng;
          const fy = Math.round(py(price))+0.5;
          if (fy < chartT || fy > chartB) return;
          ctx.beginPath(); ctx.strokeStyle = FCOLS[li]; ctx.lineWidth=1; ctx.globalAlpha=0.7;
          ctx.setLineDash(lvl===0||lvl===1?[]:[5,4]);
          ctx.moveTo(x1,fy); ctx.lineTo(x2,fy); ctx.stroke(); ctx.setLineDash([]); ctx.globalAlpha=1;
          ctx.font='8px monospace'; ctx.fillStyle=FCOLS[li]; ctx.textAlign='left';
          ctx.fillText(`${(lvl*100).toFixed(1)}%  ${fmt.price(price,symbol)}`, x2+3, fy-2);
        });
      }
      if (d.type === 'rect') {
        const x1=Math.min(d.p1.x,d.p2.x), x2=Math.max(d.p1.x,d.p2.x);
        const y1=Math.min(py(d.p1.price),py(d.p2.price)), y2=Math.max(py(d.p1.price),py(d.p2.price));
        ctx.strokeStyle='#a78bfa'; ctx.lineWidth=1; ctx.setLineDash([]);
        ctx.strokeRect(x1,y1,x2-x1,y2-y1);
        ctx.fillStyle='rgba(167,139,250,0.05)'; ctx.fillRect(x1,y1,x2-x1,y2-y1);
      }
    });

    // In-progress trendline
    if (drawingState && crosshair) {
      ctx.beginPath();
      ctx.strokeStyle = '#4DB8FF';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      ctx.moveTo(drawingState.p1.x, drawingState.p1.y);
      ctx.lineTo(crosshair.x, crosshair.y);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // ── Crosshair lines
    if (crosshair && settings.crosshairStyle !== 'none') {
      ctx.strokeStyle = settings.crosshairColor;
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.65;
      if (settings.crosshairStyle === 'cross') {
        ctx.setLineDash([3, 3]);
        // Vertical
        ctx.beginPath();
        ctx.moveTo(crosshair.x + 0.5, chartT);
        ctx.lineTo(crosshair.x + 0.5, chartB);
        ctx.stroke();
        // Horizontal (only in chart area)
        if (crosshair.y >= chartT && crosshair.y <= chartB) {
          ctx.beginPath();
          ctx.moveTo(chartL, crosshair.y + 0.5);
          ctx.lineTo(chartR, crosshair.y + 0.5);
          ctx.stroke();
        }
        ctx.setLineDash([]);
      } else {
        ctx.beginPath();
        ctx.arc(crosshair.x, crosshair.y, 4, 0, Math.PI * 2);
        ctx.fillStyle = settings.crosshairColor;
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }

    // ── Time axis
    if (settings.showTimeScale) {
      const inTimeAxis = crosshair && crosshair.y >= H - TIME_H;
      ctx.fillStyle = inTimeAxis || isScalingTime.current
        ? 'rgba(255,255,255,0.04)' : settings.bgColor;
      ctx.fillRect(0, H - TIME_H, W, TIME_H);
      if (inTimeAxis || isScalingTime.current) {
        ctx.fillStyle = 'rgba(255,255,255,0.2)';
        ctx.font = '12px monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('↔', chartR / 2, H - TIME_H / 2);
      }
      ctx.beginPath();
      ctx.strokeStyle = inTimeAxis || isScalingTime.current
        ? 'rgba(0,200,150,0.5)' : settings.scaleLinesColor;
      ctx.lineWidth = 1;
      ctx.setLineDash([]);
      ctx.moveTo(chartL, H - TIME_H + 0.5);
      ctx.lineTo(chartR, H - TIME_H + 0.5);
      ctx.stroke();

      // Time labels every ~10 bars
      const step = Math.max(1, Math.floor(vis.length / 8));
      ctx.fillStyle = settings.scaleTextColor;
      ctx.font = `${settings.scaleTextSize}px monospace`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      for (let i = 0; i < vis.length; i += step) {
        const x = bx(i);
        const bar = vis[i];
        const d = bar.t ? new Date(bar.t) : new Date();
        const label = timeframe.includes('D') || timeframe.includes('W')
          ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          : d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
        ctx.fillStyle = settings.scaleLinesColor;
        ctx.fillRect(x, H - TIME_H + 0.5, 1, 4);
        ctx.fillStyle = settings.scaleTextColor;
        ctx.fillText(label, x, H - TIME_H + 13);
      }
    }

  }, [candles, offset, zoom, priceAxisW, timeAxisH, priceOffset, crosshair, drawings, drawingState, settings, positions, symbol, isScalingPrice, isScalingTime, economicEvents]);

  // Trigger draw on every render
  useEffect(() => {
    if (animFrame.current) cancelAnimationFrame(animFrame.current);
    animFrame.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animFrame.current);
  });

  // Resize observer — keep canvas pixel-perfect
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const DPR = window.devicePixelRatio || 1;
    const resize = () => {
      const { width, height } = canvas.getBoundingClientRect();
      canvas.width = Math.round(width * DPR);
      canvas.height = Math.round(height * DPR);
      setTick(t => t + 1);
    };
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();
    return () => ro.disconnect();
  }, []);

  // ── Mouse helpers
  const canvasXY = (e) => {
    const r = canvasRef.current?.getBoundingClientRect(); if (!r) return { x: 0, y: 0 };
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  // Recompute layout values for hit-testing (must match draw())
  const getLayout = () => {
    const canvas = canvasRef.current; if (!canvas) return null;
    const DPR = window.devicePixelRatio || 1;
    const W = canvas.width / DPR;
    const H = canvas.height / DPR;
    const PRICE_W = settings.showPriceScale ? priceAxisW : 0;
    const TIME_H = settings.showTimeScale ? timeAxisH : 0;
    const VOL_H = settings.showVolume ? Math.floor(H * 0.12) : 0;
    const chartR = W - PRICE_W;
    const chartT = 2, chartB = H - TIME_H - VOL_H - 2;
    const chartH = chartB - chartT;
    const chartW = chartR;
    const total = candles.current.length;
    const vis = candles.current.slice(Math.max(0, total - zoom - offset), total - offset || undefined).slice(-zoom);
    const prices = vis.flatMap(c => [c.h, c.l]);
    const minP = Math.min(...prices), maxP = Math.max(...prices);
    const pad = (maxP - minP) * 0.06;
    const lo = minP - pad - priceOffset, hi = maxP + pad - priceOffset;
    const priceRange = hi - lo || 1;
    const barW = chartW / (vis.length || 1);
    const py = p => chartT + chartH - ((p - lo) / priceRange) * chartH;
    const bx = i => i * barW + barW / 2;
    const priceAtY = y => lo + (1 - (y - chartT) / chartH) * priceRange;
    return { chartT, chartB, chartW, chartH, barW, py, bx, priceAtY, vis };
  };

  // Which zone is cursor in?
  const getZone = (x, y) => {
    const canvas = canvasRef.current; if (!canvas) return 'chart';
    const DPR = window.devicePixelRatio || 1;
    const W = canvas.width / DPR, H = canvas.height / DPR;
    const PRICE_W = settings.showPriceScale ? priceAxisW : 0;
    const TIME_H = settings.showTimeScale ? timeAxisH : 0;
    const VOL_H = settings.showVolume ? Math.floor(H * 0.12) : 0;
    const chartR = W - PRICE_W;
    const chartB = H - TIME_H - VOL_H - 2;
    if (x >= chartR && y <= chartB) return 'price';       // right price axis
    if (y >= chartB + VOL_H && y >= H - TIME_H) return 'time'; // bottom time axis
    return 'chart';
  };

  const onMouseDown = (e) => {
    const { x, y } = canvasXY(e);
    const layout = getLayout(); if (!layout) return;
    const { priceAtY } = layout;
    const zone = getZone(x, y);

    if (zone === 'price') {
      isScalingPrice.current = true;
      dragStart.current = { y: e.clientY, startOffset: priceOffset };
      if(canvasRef.current) canvasRef.current.style.cursor='ns-resize';
      return;
    }
    if (zone === 'time') {
      isScalingTime.current = true;
      dragStart.current = { y: e.clientY, startH: timeAxisH };
      if(canvasRef.current) canvasRef.current.style.cursor='ns-resize';
      return;
    }
    if (activeTool === 'hline') {
      setDrawings(d => [...d, { type: 'hline', price: priceAtY(y), id: Date.now() }]);
      setActiveTool(null); return;
    }
    if (activeTool === 'trendline') {
      if (!drawingState) { setDrawingState({ p1: { x, y } }); }
      else {
        setDrawings(d => [...d, { type: 'trendline', p1: drawingState.p1, p2: { x, y }, id: Date.now() }]);
        setDrawingState(null); setActiveTool(null);
      }
      return;
    }
    if (activeTool === 'fib' || activeTool === 'rect') {
      if (!drawingState) { setDrawingState({ p1: { x, y, price: priceAtY(y) } }); }
      else {
        setDrawings(d => [...d, { type: activeTool, p1: drawingState.p1, p2: { x, y, price: priceAtY(y) }, id: Date.now() }]);
        setDrawingState(null); setActiveTool(null);
      }
      return;
    }
    isDragging.current = true;
    dragStart.current = { x: e.clientX, offset };
  };

  const onMouseMove = (e) => {
    const { x, y } = canvasXY(e);
    const layout = getLayout();
    if (!layout) return;
    const { barW, vis } = layout;
    const zone = getZone(x, y);

    // Cursor style based on zone or active drag
    const canvas = canvasRef.current;
    if (canvas) {
      if (isScalingPrice.current) canvas.style.cursor = 'ns-resize';
      else if (isScalingTime.current) canvas.style.cursor = 'ns-resize';
      else if (zone === 'price') canvas.style.cursor = 'ns-resize';
      else if (zone === 'time') canvas.style.cursor = 'ns-resize';
      else if (activeTool) canvas.style.cursor = 'crosshair';
      else if (isDragging.current) canvas.style.cursor = 'grabbing';
      else canvas.style.cursor = 'default';
    }

    // Price axis drag → pan price range up/down
    if (isScalingPrice.current && dragStart.current) {
      const layout2 = getLayout(); if (!layout2) return;
      const { chartH } = layout2;
      const prices2 = layout2.vis.flatMap(c => [c.h, c.l]);
      const range = Math.max(...prices2) - Math.min(...prices2) || 1;
      const dy = e.clientY - dragStart.current.y;
      const pricePerPx = (range * 1.12) / chartH;
      setPriceOffset(dragStart.current.startOffset + dy * pricePerPx);
      return;
    }

    // Time axis drag → taller/shorter the bottom time bar
    if (isScalingTime.current && dragStart.current) {
      const dy = dragStart.current.y - e.clientY;  // drag up = taller
      setTimeAxisH(Math.max(16, Math.min(60, dragStart.current.startH + dy)));
      return;
    }

    setCrosshair({ x, y });
    const ci = Math.floor(x / barW);
    if (ci >= 0 && ci < vis.length) {
      setOhlc(vis[ci]);
      window.dispatchEvent(new CustomEvent('chart:ohlc', { detail: vis[ci] }));
      // Emit RSI for sub-panel
      if (indicators.some(i=>i.type==='RSI'&&i.visible)) {
        const rsiPeriod = indicators.find(i=>i.type==='RSI'&&i.visible)?.period||14;
        const rsiVals = calcRSI(vis, rsiPeriod);
        window.dispatchEvent(new CustomEvent('chart:rsi', { detail: { rsi: rsiVals } }));
      }
    }

    if (isDragging.current && !activeTool) {
      const dx = e.clientX - dragStart.current.x;
      const delta = Math.round(dx / barW);
      const total = candles.current.length;
      setOffset(Math.max(0, Math.min(total - zoom, dragStart.current.offset - delta)));
    }
  };

  const onMouseUp = () => {
    isDragging.current = false;
    isScalingPrice.current = false;
    isScalingTime.current = false;
  };

  const handleClick = (e) => {
    const { x, y } = canvasXY(e);
    const zone = getZone(x, y);
    // Double-click on axes = reset zoom
    if (e.detail === 2) {
      if (zone === 'price') { setPriceOffset(0); return; }
      if (zone === 'time')  { setTimeAxisH(24); return; }
    }
    if (e.detail > 1) return;
    const layout = getLayout(); if (!layout) return;
    const { py } = layout;
    if (zone !== 'chart') return;
    if (!activeTool) {
      setDrawings(prev => {
        for (let i = prev.length - 1; i >= 0; i--) {
          const d = prev[i];
          if (d.type === 'hline') {
            const dy = py(d.price);
            if (Math.abs(y - dy) < 6) return prev.filter((_, j) => j !== i);
          }
        }
        return prev;
      });
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', background: settings.bgColor }}>

      {/* Drawing tools */}
      <div style={{ position: 'absolute', top: 6, left: 8, zIndex: 10, display: 'flex', gap: 3, alignItems: 'center' }}>
        {[{ t: 'hline', i: '—', tt: 'Horizontal Line (H)' }, { t: 'trendline', i: '╱', tt: 'Trendline (T)' }].map(({ t, i, tt }) => (
          <button key={t} title={tt} onClick={() => setActiveTool(at => at === t ? null : t)} style={{
            width: 26, height: 26, borderRadius: 3, cursor: 'pointer', fontSize: 13,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: activeTool === t ? 'rgba(245,166,35,0.18)' : 'rgba(0,0,0,0.55)',
            border: activeTool === t ? '1px solid rgba(245,166,35,0.6)' : '1px solid rgba(255,255,255,0.1)',
            color: activeTool === t ? '#F5A623' : '#555',
          }}>{i}</button>
        ))}
        <button title="Clear all (D)" onClick={() => setDrawings([])} style={{ width: 26, height: 26, borderRadius: 3, cursor: 'pointer', fontSize: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.1)', color: '#444' }}>✕</button>
        {activeTool && (
          <span style={{ fontFamily: 'monospace', fontSize: 9, color: '#F5A623', padding: '0 8px', display: 'flex', alignItems: 'center', background: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.3)', borderRadius: 3, height: 26 }}>
            {activeTool === 'hline' ? 'CLICK TO PLACE' : drawingState ? 'CLICK END POINT' : 'CLICK START POINT'} · ESC cancel
          </span>
        )}
      </div>

      {/* OHLC status line */}
      {settings.showOHLC && ohlc && (
        <div style={{ position: 'absolute', top: 8, left: 104, zIndex: 10, display: 'flex', gap: 10, fontFamily: 'monospace', fontSize: 9, pointerEvents: 'none', alignItems: 'center' }}>
          {[['O', ohlc.o, '#888'], ['H', ohlc.h, settings.upColor], ['L', ohlc.l, settings.downColor], ['C', ohlc.c, ohlc.c >= ohlc.o ? settings.upColor : settings.downColor]].map(([l, v, c]) => (
            <span key={l} style={{ color: '#444' }}>{l} <span style={{ color: c, fontWeight: 600 }}>{fmt.price(v, symbol)}</span></span>
          ))}
          <span style={{ color: '#333', marginLeft: 4 }}>
            {ohlc.c >= ohlc.o ? '+' : ''}{((ohlc.c - ohlc.o)).toFixed(2)}
            {' '}<span style={{ color: ohlc.c >= ohlc.o ? settings.upColor : settings.downColor }}>
              ({ohlc.c >= ohlc.o ? '+' : ''}{(((ohlc.c - ohlc.o) / ohlc.o) * 100).toFixed(2)}%)
            </span>
          </span>
        </div>
      )}

      <canvas
        ref={canvasRef}
        style={{
          display: 'block', width: '100%', height: '100%',
          cursor: activeTool ? 'crosshair' : isDragging.current ? 'grabbing' : 'default',
        }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={() => { onMouseUp(); setCrosshair(null); setOhlc(null); }}
        onClick={handleClick}
        onContextMenu={e=>{
          e.preventDefault();
          const {x,y} = canvasXY(e);
          const layout = getLayout();
          if(layout){
            const {py:pyFn} = layout;
            // Hit-test drawings for right-click delete
            const hit = drawings.find(d=>{
              if(d.type==='hline') return Math.abs(pyFn(d.price)-y)<8;
              if(d.type==='fib'||d.type==='rect'){
                const hi=Math.max(d.p1?.price||0,d.p2?.price||0);
                const lo=Math.min(d.p1?.price||0,d.p2?.price||0);
                const cp=layout.priceAtY(y);
                const lx=Math.min(d.p1?.x||0,d.p2?.x||0);
                const rx=Math.max(d.p1?.x||0,d.p2?.x||0);
                return cp>=lo&&cp<=hi&&x>=lx-4&&x<=rx+4;
              }
              if(d.type==='trendline'){
                // point-to-line distance
                const dx=d.p2.x-d.p1.x, dy=d.p2.y-d.p1.y;
                const len=Math.sqrt(dx*dx+dy*dy)||1;
                const dist=Math.abs(dy*(x-d.p1.x)-dx*(y-d.p1.y))/len;
                return dist<8;
              }
              return false;
            });
            if(hit){setDrawings(ds=>ds.filter(d=>d.id!==hit.id)); return;}
          }
          onContextMenu(e);
        }}
      />
    </div>
  );
};


export { CandleChart };
